import { useState, type FormEvent } from 'react';
import { DraftNotice } from '../../components/DraftNotice';
import { Modal } from '../../components/Modal';
import { planErrorMessage } from '../../components/plan/planErrors';
import { TicketHead } from '../../components/TicketHead';
import { clearDraft, knowledgeBaseDraftKey, restoreDraft } from '../../lib/draftStorage';
import { formatDate } from '../../lib/format';
import { useDraft } from '../../lib/useDraft';
import type { CalendarEventCreate, CalendarEventRead } from '../../types/api';
import { calendarEventsApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';

/** Autosaved shape. Field names are the form's, not the API's. */
interface CalendarEventDraft {
  title: string;
  description: string;
  startsOn: string;
  endsOn: string;
}

function calendarDraftKey(businessId: string, item: CalendarEventRead | null): string {
  return knowledgeBaseDraftKey('calendar', businessId, item?.id ?? null);
}

export interface CalendarEventFormProps {
  businessId: string;
  initial: CalendarEventRead | null;
  isSaving: boolean;
  error: string | null;
  /** Start date a create form opens on, e.g. the calendar day that was clicked. */
  defaultStartsOn?: string;
  onSubmit: (body: CalendarEventCreate) => void;
  onCancel: () => void;
}

/**
 * The one key-date form. Lives here because the knowledge base owns the record,
 * and is exported because the content calendar edits the same rows in a modal of
 * its own — a second copy would be a second set of validation rules.
 */
export function CalendarEventForm({
  businessId,
  initial,
  isSaving,
  error,
  defaultStartsOn = '',
  onSubmit,
  onCancel,
}: CalendarEventFormProps) {
  const draftKey = calendarDraftKey(businessId, initial);
  const pristine: CalendarEventDraft = {
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    startsOn: initial?.starts_on ?? defaultStartsOn,
    endsOn: initial?.ends_on ?? '',
  };
  // The form mounts when the modal opens, so this initialiser is the "on open"
  // hook — no effect, no second render.
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [title, setTitle] = useState(restored.value.title);
  const [description, setDescription] = useState(restored.value.description);
  const [startsOn, setStartsOn] = useState(restored.value.startsOn);
  const [endsOn, setEndsOn] = useState(restored.value.endsOn);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useDraft(draftKey, { title, description, startsOn, endsOn }, pristine);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!title.trim()) {
      setFieldError('Name the event before saving.');
      return;
    }
    if (!startsOn) {
      setFieldError('Pick a start date.');
      return;
    }
    if (endsOn && endsOn < startsOn) {
      setFieldError('The end date cannot fall before the start date.');
      return;
    }
    setFieldError(null);
    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      starts_on: startsOn,
      ends_on: endsOn || null,
    });
  }

  /**
   * Cancel is a decision to throw the work away. Escape and the header Close
   * are not — those leave the draft where it is.
   */
  function handleCancel() {
    clearDraft(draftKey);
    onCancel();
  }

  const message = fieldError ?? error;

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {restoredAt ? (
        <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
      ) : null}

      <label className="field">
        <span className="field__label">Title</span>
        <input
          className="field__input"
          type="text"
          maxLength={200}
          placeholder="Spring blend launch"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Description</span>
        <textarea
          className="field__input"
          rows={4}
          placeholder="What is happening, and what the posts should push."
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="kb-form__row">
        <label className="field">
          <span className="field__label">Starts on</span>
          <input
            className="field__input"
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Ends on</span>
          <input
            className="field__input"
            type="date"
            value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)}
          />
          <span className="field__hint">Leave blank for a single day.</span>
        </label>
      </div>

      {message ? (
        <p className="form__error" role="alert">
          {message}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </button>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : 'Save event'}
        </button>
      </div>
    </form>
  );
}

/** Also the subject of the cap message. */
const SECTION_TITLE = 'Calendar events';

/** Calendar events — dated hooks the generator can build timely content around. */
export function CalendarEventsSection({ businessId }: { businessId: string }) {
  const collection = useCollection(calendarEventsApi, businessId, 'calendar_events');
  const { capacity, isFull, planName, fullTitle, refreshCapacity } = useSectionCapacity(
    businessId,
    'key_dates',
  );
  const [editing, setEditing] = useState<CalendarEventRead | null | 'new'>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  function startCreate() {
    setWriteError(null);
    setEditing('new');
  }

  async function handleSubmit(body: CalendarEventCreate) {
    const draftKey = calendarDraftKey(businessId, editing === 'new' ? null : editing);
    setIsSaving(true);
    setWriteError(null);
    try {
      if (editing && editing !== 'new') {
        await collection.update(editing.id, body);
      } else {
        await collection.create(body);
        refreshCapacity();
      }
      // The record is on file; the local copy has nothing left to protect.
      clearDraft(draftKey);
      setEditing(null);
    } catch (caught) {
      // A 402 at the cap: the counts were stale, so re-read them too.
      refreshCapacity();
      setWriteError(
        planErrorMessage(caught, 'That event did not save.', {
          plan: planName,
          subject: SECTION_TITLE,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the row goes now, the DELETE waits out the undo window. */
  function startDelete(item: CalendarEventRead) {
    collection.removeWithUndo({
      id: item.id,
      label: item.title,
      failureMessage: 'That event could not be deleted.',
      onRemoved: refreshCapacity,
    });
  }

  return (
    <>
      <SectionShell
        title={SECTION_TITLE}
        lede="Launches, seasons and promotions the content should line up with."
        action={
          <button
            className="button button--primary"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            Add event
          </button>
        }
        capacity={capacity}
        isLoading={collection.isLoading}
        error={collection.error ?? collection.deleteError}
        isEmpty={collection.items.length === 0}
        emptyMessage="No events yet. Add the next thing on the calendar."
        emptyAction={
          <button
            className="button"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            Add event
          </button>
        }
      >
        <ul className="record-list">
          {collection.items.map((item) => (
            <li className="record" key={item.id}>
              <TicketHead label="Event" timestamp={item.created_at} />
              <div className="record__head">
                <span className="record__title">{item.title}</span>
                <span className="record__meta">
                  {formatDate(item.starts_on)}
                  {item.ends_on ? ` – ${formatDate(item.ends_on)}` : ''}
                </span>
              </div>
              {item.description ? <p className="record__body">{item.description}</p> : null}
              <div className="record__actions">
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => {
                    setWriteError(null);
                    setEditing(item);
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => startDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      {editing ? (
        <Modal
          title={editing === 'new' ? 'Add calendar event' : 'Edit calendar event'}
          kind={editing === 'new' ? 'new calendar event' : 'calendar event'}
          onDismiss={() => setEditing(null)}
        >
          <CalendarEventForm
            businessId={businessId}
            initial={editing === 'new' ? null : editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      ) : null}
    </>
  );
}
